/**
 * React hooks for notification preferences
 * Integrates with TanStack Query for caching and state management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import * as notificationsService from '../services/notifications';
import * as onesignalService from '../services/onesignal';
import type {
  NotificationPreferencesUpdate,
  EventNotificationSubscriptionUpdate,
} from '../types/notifications.types';
import { ReminderTimeOption } from '../types/notifications.types';

// Query keys for consistent cache management
export const notificationKeys = {
  all: ['notifications'] as const,
  preferencesRoot: () => [...notificationKeys.all, 'preferences'] as const,
  preferences: (userId: string) =>
    [...notificationKeys.preferencesRoot(), userId] as const,
  eventSubscriptionRoot: () =>
    [...notificationKeys.all, 'eventSubscription'] as const,
  eventSubscription: (userId: string, eventId: number) =>
    [...notificationKeys.eventSubscriptionRoot(), userId, eventId] as const,
};

// ============================================================================
// Global Notification Preferences
// ============================================================================

/**
 * Fetch and manage the current user's global notification preferences
 */
export function useNotificationPreferences() {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: notificationKeys.preferences(user?.id || ''),
    queryFn: () => notificationsService.getNotificationPreferences(user?.id),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  const mutation = useMutation({
    mutationFn: async (preferences: NotificationPreferencesUpdate) => {
      // Update Supabase
      const updated = await notificationsService.upsertNotificationPreferences(
        user?.id,
        preferences
      );

      // Sync to OneSignal tags
      const tags = onesignalService.preferencesToTags({
        notif_event_reminders: updated.notif_event_reminders,
        notif_event_changes: updated.notif_event_changes,
      });
      await onesignalService.updateNotificationTags(tags);

      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationKeys.preferencesRoot(),
      });
    },
  });

  // Default preferences if none exist
  const defaultPreferences = {
    notif_event_reminders: true,
    notif_event_changes: true,
    notif_community_comments: true,
  };

  return {
    ...query,
    preferences: query.data ?? defaultPreferences,
    updatePreferences: mutation.mutateAsync,
    isUpdating: mutation.isPending,
  };
}

// ============================================================================
// Per-Event Notification Subscriptions
// ============================================================================

/**
 * Fetch and manage the current user's subscription for a specific event
 */
export function useEventNotificationSubscription(eventId: number) {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: notificationKeys.eventSubscription(user?.id || '', eventId),
    queryFn: () => notificationsService.getEventSubscription(user?.id, eventId),
    enabled: isAuthenticated && eventId > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const upsertMutation = useMutation({
    mutationFn: (subscription: EventNotificationSubscriptionUpdate) =>
      notificationsService.upsertEventSubscription(
        user?.id,
        eventId,
        subscription
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationKeys.eventSubscriptionRoot(),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      notificationsService.deleteEventSubscription(user?.id, eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationKeys.eventSubscriptionRoot(),
      });
    },
  });

  // Convert current subscription to array of selected reminders
  const selectedReminders =
    notificationsService.subscriptionToSelectedReminders(query.data ?? null);

  // Update reminders based on multi-select (array of ReminderTimeOptions)
  const setReminders = async (options: ReminderTimeOption[]) => {
    const subscriptionUpdate =
      notificationsService.selectedRemindersToSubscription(options);
    await upsertMutation.mutateAsync(subscriptionUpdate);
  };

  // Toggle a single reminder option on/off
  const toggleReminder = async (option: ReminderTimeOption) => {
    const newSelection = selectedReminders.includes(option)
      ? selectedReminders.filter((r) => r !== option)
      : [...selectedReminders, option];
    await setReminders(newSelection);
  };

  return {
    ...query,
    subscription: query.data,
    selectedReminders,
    setReminders,
    toggleReminder,
    updateSubscription: upsertMutation.mutateAsync,
    deleteSubscription: deleteMutation.mutateAsync,
    isUpdating: upsertMutation.isPending || deleteMutation.isPending,
  };
}
